import {
  useState,
  useRef,
  useCallback,
  useEffect
} from 'react'

import {
  motion,
  AnimatePresence
} from 'framer-motion'

import {
  FaFilePdf,
  FaUpload,
  FaDownload,
  FaTrash,
  FaCheck,
  FaExclamationTriangle,
  FaEye,
  FaCloudUploadAlt,
  FaFileAlt
} from 'react-icons/fa'

import {
  getResume,
  saveResume,
  deleteResume
} from '../../../api/portfolioApi'

import {
  useAdmin
} from '../../../context/AdminContext'

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const MAX_SIZE_MB = 5

const MAX_SIZE_B =
  MAX_SIZE_MB * 1024 * 1024

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function formatBytes(bytes) {

  if (!bytes) return '0 B'

  if (bytes < 1024) {
    return `${bytes} B`
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  }

  return `${(
    bytes / (1024 * 1024)
  ).toFixed(2)} MB`
}

function formatDate(iso) {

  if (!iso) return '—'

  return new Date(iso).toLocaleString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
  )
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function ResumeEditor({
  onSaved
}) {

  // ───────────────────────────────────────────
  // Context
  // ───────────────────────────────────────────
  const {
    resumeData,
    siteData,
    refreshPortfolio
  } = useAdmin()

  // ───────────────────────────────────────────
  // Local State
  // ───────────────────────────────────────────
  const [pending, setPending] =
    useState(null)

  const [dragging, setDragging] =
    useState(false)

  const [err, setErr] =
    useState('')

  const [loading, setLoading] =
    useState(false)

  const [confirmDel, setConfirmDel] =
    useState(false)

  const fileRef = useRef(null)

  // ───────────────────────────────────────────
  // Validate
  // ───────────────────────────────────────────
  const validate = (file) => {

    if (!file) {
      return 'No file selected.'
    }

    if (
      file.type !==
      'application/pdf'
    ) {

      return `
        Only PDF files are allowed.
      `
    }

    if (file.size > MAX_SIZE_B) {

      return `
        File too large.
        Max ${MAX_SIZE_MB} MB allowed.
      `
    }

    return null
  }

  // ───────────────────────────────────────────
  // Process File
  // ───────────────────────────────────────────
  const processFile =
    useCallback((file) => {

      const validationError =
        validate(file)

      if (validationError) {

        setErr(validationError)

        return
      }

      setErr('')

      setLoading(true)

      const reader =
        new FileReader()

      reader.onload = (e) => {

        setPending({

          dataUrl:
            e.target.result,

          fileName:
            file.name,

          sizeBytes:
            file.size
        })

        setLoading(false)
      }

      reader.onerror = () => {

        setErr(
          'Could not read file.'
        )

        setLoading(false)
      }

      reader.readAsDataURL(file)

    }, [])

  // ───────────────────────────────────────────
  // File Input
  // ───────────────────────────────────────────
  const handleFileChange = (e) => {

    const file =
      e.target.files?.[0]

    if (file) {
      processFile(file)
    }

    e.target.value = ''
  }

  // ───────────────────────────────────────────
  // Drag & Drop
  // ───────────────────────────────────────────
  const handleDrop = (e) => {

    e.preventDefault()

    setDragging(false)

    const file =
      e.dataTransfer.files?.[0]

    if (file) {
      processFile(file)
    }
  }

  // ───────────────────────────────────────────
  // Save Resume
  // ───────────────────────────────────────────
  const handleSave =
    async () => {

      if (!pending) return

      try {

        setLoading(true)

        setErr('')

        // Save to backend
        await saveResume(
          pending.dataUrl,
          pending.fileName
        )

        // Refresh homepage state
        await refreshPortfolio()

        setPending(null)

        onSaved?.(
          `Resume "${pending.fileName}" uploaded!`
        )

      } catch (err) {

        console.error(err)

        setErr(
          err.message
          || 'Failed to upload resume'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Delete Resume
  // ───────────────────────────────────────────
  const handleDelete =
    async () => {

      try {

        setLoading(true)

        setErr('')

        // Delete from backend
        await deleteResume()

        // Refresh homepage state
        await refreshPortfolio()

        setPending(null)

        setConfirmDel(false)

        onSaved?.(
          'Resume removed.'
        )

      } catch (err) {

        console.error(err)

        setErr(
          err.message
          || 'Failed to remove resume'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Download
  // ───────────────────────────────────────────
  const triggerDownload = (
    dataUrl,
    fileName
  ) => {

    const a =
      document.createElement('a')

    a.href = dataUrl

    a.download =
      fileName ||
      'Resume.pdf'

    a.rel =
      'noopener noreferrer'

    document.body.appendChild(a)

    a.click()

    document.body.removeChild(a)
  }

  // ───────────────────────────────────────────
  // Preview
  // ───────────────────────────────────────────
  const handlePreview = (
    dataUrl
  ) => {

    const win =
      window.open()

    if (!win) return

    win.document.write(`
      <iframe
        src="${dataUrl}"
        width="100%"
        height="100%"
        style="border:none"
      ></iframe>
    `)
  }

  // ───────────────────────────────────────────
  // Download Name
  // ───────────────────────────────────────────
  const ownerName =
    siteData?.hero?.name ||
    'Resume'

  const downloadName =
    `${ownerName.replace(
      /\s+/g,
      '_'
    )}_Resume.pdf`

  // ───────────────────────────────────────────
  // UI
  // ───────────────────────────────────────────
  return (

    <div
      className="
        glass-card
        rounded-2xl
        border
        border-white/5
        overflow-hidden
      "
    >

      {/* Header */}
      <div
        className="
          flex
          items-center
          justify-between
          px-6
          py-4
          border-b
          border-white/5
        "
      >

        <div>

          <h3
            className="
              font-display
              font-semibold
              text-white
              flex
              items-center
              gap-2
            "
          >

            <FaFilePdf
              size={14}
              className="
                text-red-400
              "
            />

            Resume / CV

          </h3>

          <p
            className="
              text-xs
              text-slate-500
              mt-0.5
              font-mono
            "
          >

            PDF only ·
            max {MAX_SIZE_MB} MB

          </p>

        </div>

        {resumeData && (

          <span
            className="
              flex
              items-center
              gap-1.5
              text-xs
              font-mono
              text-green-400
              px-2.5
              py-1
              rounded-lg
              bg-green-500/10
              border
              border-green-500/20
            "
          >

            <FaCheck size={9} />

            Uploaded

          </span>
        )}

      </div>

      {/* Body */}
      <div className="p-6 space-y-5">

        {/* Upload Box */}
        {!pending && (

          <motion.div

            whileHover={{
              scale: 1.01
            }}

            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}

            onDragLeave={() => {
              setDragging(false)
            }}

            onDrop={handleDrop}

            onClick={() =>
              fileRef.current?.click()
            }

            className="
              border-2
              border-dashed
              rounded-2xl
              p-8
              text-center
              cursor-pointer
              transition-all
            "

            style={{
              borderColor:
                dragging

                  ? '#6366f1'

                  : 'rgba(255,255,255,0.1)',

              background:
                dragging

                  ? 'rgba(99,102,241,0.05)'

                  : 'rgba(255,255,255,0.02)'
            }}
          >

            <FaCloudUploadAlt
              size={28}
              className="
                mx-auto
                text-slate-500
                mb-3
              "
            />

            <p
              className="
                text-sm
                text-slate-300
                font-medium
              "
            >
              Drag & drop PDF here
            </p>

            <p
              className="
                text-xs
                text-slate-600
                mt-1
                font-mono
              "
            >
              or click to browse
            </p>

          </motion.div>
        )}

        {/* Pending Upload */}
        {pending && (

          <motion.div

            initial={{
              opacity: 0,
              y: 10
            }}

            animate={{
              opacity: 1,
              y: 0
            }}

            className="
              rounded-2xl
              border
              border-blue-500/20
              bg-blue-500/5
              p-5
            "
          >

            <div
              className="
                flex
                items-start
                gap-4
              "
            >

              <div
                className="
                  w-14
                  h-14
                  rounded-2xl
                  flex
                  items-center
                  justify-center
                  text-2xl
                  flex-shrink-0
                "

                style={{
                  background:
                    'rgba(99,102,241,0.1)',

                  border:
                    '1px solid rgba(99,102,241,0.2)'
                }}
              >
                📄
              </div>

              <div className="flex-1 min-w-0">

                <p
                  className="
                    text-sm
                    font-semibold
                    text-white
                    truncate
                  "
                >
                  {pending.fileName}
                </p>

                <p
                  className="
                    text-xs
                    text-slate-500
                    mt-1
                    font-mono
                  "
                >
                  {formatBytes(
                    pending.sizeBytes
                  )}
                </p>

              </div>

            </div>

            {/* Actions */}
            <div
              className="
                flex
                gap-3
                mt-5
                flex-wrap
              "
            >

              <motion.button

                onClick={handleSave}

                whileTap={{
                  scale: 0.97
                }}

                className="
                  flex
                  items-center
                  gap-2
                  px-4
                  py-2
                  rounded-xl
                  text-sm
                  font-semibold
                  text-white
                "

                style={{
                  background:
                    'linear-gradient(135deg,#6366f1,#8b5cf6)'
                }}
              >

                <FaUpload size={11} />

                {loading
                  ? 'Uploading...'
                  : 'Upload Resume'}

              </motion.button>

              <motion.button

                onClick={() =>
                  setPending(null)
                }

                whileTap={{
                  scale: 0.97
                }}

                className="
                  px-4
                  py-2
                  rounded-xl
                  text-sm
                  border
                  border-white/10
                  text-slate-400
                "
              >

                Cancel

              </motion.button>

            </div>

          </motion.div>
        )}

        {/* Current Resume */}
        {resumeData && !pending && (

          <motion.div

            initial={{
              opacity: 0,
              y: 8
            }}

            animate={{
              opacity: 1,
              y: 0
            }}

            className="
              rounded-2xl
              border
              p-5
              flex
              items-start
              gap-4
            "

            style={{
              background:
                'linear-gradient(135deg,rgba(239,68,68,0.05),rgba(99,102,241,0.05))',

              borderColor:
                'rgba(239,68,68,0.18)'
            }}
          >

            <div
              className="
                w-14
                h-14
                rounded-2xl
                flex
                items-center
                justify-center
                flex-shrink-0
                text-2xl
              "

              style={{
                background:
                  'rgba(239,68,68,0.12)',

                border:
                  '1px solid rgba(239,68,68,0.2)'
              }}
            >
              📄
            </div>

            <div className="flex-1 min-w-0">

              <p
                className="
                  font-display
                  font-bold
                  text-white
                  text-sm
                  truncate
                "
              >
                {resumeData.fileName}
              </p>

              <p
                className="
                  font-mono
                  text-xs
                  text-slate-500
                  mt-1
                "
              >

                Uploaded:
                {' '}
                {formatDate(
                  resumeData.uploadedAt
                )}

              </p>

            </div>

            {/* Actions */}
            <div
              className="
                flex
                flex-col
                gap-2
              "
            >

              <motion.button

                onClick={() =>
                  handlePreview(
                    resumeData.dataUrl
                  )
                }

                className="
                  flex
                  items-center
                  gap-1.5
                  px-3
                  py-1.5
                  rounded-xl
                  text-xs
                  font-mono
                "

                style={{
                  background:
                    'rgba(99,102,241,0.1)',

                  border:
                    '1px solid rgba(99,102,241,0.2)',

                  color:
                    '#818cf8'
                }}
              >

                <FaEye size={10} />

                Preview

              </motion.button>

              <motion.button

                onClick={() =>
                  triggerDownload(
                    resumeData.dataUrl,
                    downloadName
                  )
                }

                className="
                  flex
                  items-center
                  gap-1.5
                  px-3
                  py-1.5
                  rounded-xl
                  text-xs
                  font-mono
                "

                style={{
                  background:
                    'rgba(16,185,129,0.1)',

                  border:
                    '1px solid rgba(16,185,129,0.2)',

                  color:
                    '#34d399'
                }}
              >

                <FaDownload size={10} />

                Download

              </motion.button>

              <motion.button

                onClick={() =>
                  setConfirmDel(true)
                }

                className="
                  flex
                  items-center
                  gap-1.5
                  px-3
                  py-1.5
                  rounded-xl
                  text-xs
                  font-mono
                "

                style={{
                  background:
                    'rgba(239,68,68,0.1)',

                  border:
                    '1px solid rgba(239,68,68,0.2)',

                  color:
                    '#f87171'
                }}
              >

                <FaTrash size={10} />

                Delete

              </motion.button>

            </div>

          </motion.div>
        )}

        {/* Delete Confirm */}
        <AnimatePresence>

          {confirmDel && (

            <motion.div

              initial={{
                opacity: 0
              }}

              animate={{
                opacity: 1
              }}

              exit={{
                opacity: 0
              }}

              className="
                flex
                items-center
                justify-between
                gap-4
                rounded-xl
                border
                border-red-500/20
                bg-red-500/5
                px-4
                py-3
              "
            >

              <p
                className="
                  text-sm
                  text-red-400
                  font-mono
                "
              >
                Delete current resume?
              </p>

              <div className="flex gap-2">

                <button

                  onClick={handleDelete}

                  className="
                    px-3
                    py-1.5
                    rounded-lg
                    text-xs
                    bg-red-500/15
                    border
                    border-red-500/20
                    text-red-400
                  "
                >
                  Yes
                </button>

                <button

                  onClick={() =>
                    setConfirmDel(false)
                  }

                  className="
                    px-3
                    py-1.5
                    rounded-lg
                    text-xs
                    border
                    border-white/10
                    text-slate-400
                  "
                >
                  Cancel
                </button>

              </div>

            </motion.div>
          )}

        </AnimatePresence>

        {/* Error */}
        <AnimatePresence>

          {err && (

            <motion.div

              initial={{
                opacity: 0,
                height: 0
              }}

              animate={{
                opacity: 1,
                height: 'auto'
              }}

              exit={{
                opacity: 0,
                height: 0
              }}

              className="
                flex
                items-center
                gap-2.5
                px-4
                py-3
                rounded-xl
                text-sm
                font-mono
              "

              style={{
                background:
                  'rgba(248,113,113,0.08)',

                border:
                  '1px solid rgba(248,113,113,0.2)',

                color:
                  '#f87171'
              }}
            >

              <FaExclamationTriangle
                size={12}
              />

              {err}

            </motion.div>
          )}

        </AnimatePresence>

        {/* Hidden Input */}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          className="hidden"
        />

      </div>

    </div>
  )
}